import React from "react";
import { Sl } from "./Select";

// Rows-per-page + "X–Y of Z entries" + Previous/numbered-pages/Next footer
// for any client-side-paginated list. Caller owns the actual slicing —
// this only renders controls and reports page/pageSize changes back up.
// Collapses to a simple Previous/Next pair (no page numbers) once there
// are more pages than fit comfortably, rather than rendering unbounded
// page buttons for a very long list.
const MAX_PAGE_BUTTONS = 5;

export const Pagination = ({ total, page, pageSize, onPageChange, onPageSizeChange, pageSizeOptions = [5, 10, 25, 50] }) => {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const clampedPage = Math.min(Math.max(1, page), pageCount);
  const start = total === 0 ? 0 : (clampedPage - 1) * pageSize + 1;
  const end = Math.min(clampedPage * pageSize, total);

  const pages = [];
  if (pageCount <= MAX_PAGE_BUTTONS) {
    for (let p = 1; p <= pageCount; p++) pages.push(p);
  } else {
    const half = Math.floor(MAX_PAGE_BUTTONS / 2);
    let lo = Math.max(1, clampedPage - half);
    let hi = Math.min(pageCount, lo + MAX_PAGE_BUTTONS - 1);
    lo = Math.max(1, hi - MAX_PAGE_BUTTONS + 1);
    for (let p = lo; p <= hi; p++) pages.push(p);
  }

  return (
    <div className="px-4 py-3 border-t border-b0 bg-bg1 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-t3">
      <div className="flex items-center gap-2">
        <span>Rows per page:</span>
        <Sl w={70} options={pageSizeOptions.map(n => ({ v: String(n), l: String(n) }))} value={String(pageSize)} onChange={v => onPageSizeChange(parseInt(v, 10))} />
        <span className="ml-2">{start}{"–"}{end} of {total} entries</span>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(clampedPage - 1)}
          disabled={clampedPage <= 1}
          className="px-2.5 py-1 text-t2 hover:text-t0 border border-b0 rounded-lg hover:bg-sh disabled:opacity-40 disabled:cursor-not-allowed transition"
        >Previous</button>
        {pages[0] > 1 && <span className="px-1 text-t3">…</span>}
        {pages.map(p => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={`w-7 h-7 rounded-lg text-xs flex items-center justify-center transition ${p === clampedPage ? "bg-p text-white font-semibold shadow-sm" : "text-t1 hover:bg-sh"}`}
          >{p}</button>
        ))}
        {pages[pages.length - 1] < pageCount && <span className="px-1 text-t3">…</span>}
        <button
          onClick={() => onPageChange(clampedPage + 1)}
          disabled={clampedPage >= pageCount}
          className="px-2.5 py-1 text-t2 hover:text-t0 border border-b0 rounded-lg hover:bg-sh disabled:opacity-40 disabled:cursor-not-allowed transition"
        >Next</button>
      </div>
    </div>
  );
};

// Small hook so callers don't hand-roll page/pageSize state + slicing.
// Resets to page 1 whenever the underlying data length changes (a filter
// or search narrowing the list shouldn't leave the view stranded on a
// now-nonexistent page).
export const usePagination = (data, defaultPageSize = 10) => {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);
  React.useEffect(() => { setPage(1); }, [data.length]);
  const pageCount = Math.max(1, Math.ceil(data.length / pageSize));
  const clampedPage = Math.min(page, pageCount);
  const pageData = data.slice((clampedPage - 1) * pageSize, clampedPage * pageSize);
  return {
    pageData, page: clampedPage, pageSize,
    onPageChange: setPage,
    onPageSizeChange: (n) => { setPageSize(n); setPage(1); },
  };
};
